/**
 * Muhaha Create (WebCanvas editor) handoff.
 *
 * Creation, remix and editing all live in the editor now; this app only builds
 * the links that carry a creator over to it.
 */

/**
 * The editor's origin. Every handoff starts there, so this is required for
 * `/create` and for editing a published work to work at all.
 */
const EDITOR_BASE_URL = import.meta.env.VITE_MUHAHA_NOW_BASE_URL;

/**
 * Where "编辑作品" points: the editor resolves the playable to the canvas it is
 * already bound to, and imports the game into a fresh project when there is no
 * binding yet (games made before the handoff, or in the app). Null only when no
 * editor origin is configured.
 */
export function muhahaNowEditHref(playableId: string): string | null {
	const base = editorBaseUrl();
	return base ? `${base}/canvas/playables/${encodeURIComponent(playableId)}` : null;
}

function editorBaseUrl(): string | null {
	return EDITOR_BASE_URL?.replace(/\/+$/, "") || null;
}

/**
 * Where explicit Create navigation goes. Not the bare origin: the editor's
 * landing page is one of the few routes it serves anonymously, so a visitor
 * this app only ever sends there signed in would arrive at a signed-out
 * landing and have to sign in again. `/login` is the entry that settles the
 * session first — it swaps itself for the landing when the editor already has
 * one, and otherwise runs the SSO round trip (silent, because the central
 * session this app holds is what /login authorizes against) and lands there
 * signed in.
 */
export function muhahaNowHomeHref(): string | null {
	const base = editorBaseUrl();
	return base ? `${base}/login` : null;
}

/** The editor project list where unfinished work now lives. */
export function muhahaNowProjectsHref(): string | null {
	const base = editorBaseUrl();
	return base ? `${base}/canvas/projects` : null;
}

/** An attachment already uploaded to hive, as the editor's agent references it. */
export interface MuhahaNowAttachment {
	url: string;
	mime: string;
}

/**
 * Where `/create` sends a creator once they hit send: the editor's welcome
 * console, which owns the whole generation flow now. It reads these params,
 * creates a project, and opens that project's canvas with the prompt already
 * submitted to its agent — so this app never starts a generation itself.
 *
 * `remixPlayableId` switches the editor to its import path: the source game's
 * wahaha project is imported into the new project first, and the prompt is
 * applied on top of it. `remixTitle` rides along as the name for the workspace
 * the editor creates, so a remix opens under the game it came from rather than
 * under the prompt describing the change.
 *
 * Null when no editor origin is configured — a handoff we cannot build has to
 * surface as an error rather than a navigation to nowhere.
 */
export function muhahaNowStartHref(input: {
	prompt: string;
	attachments?: MuhahaNowAttachment[];
	remixPlayableId?: string;
	remixTitle?: string;
}): string | null {
	const base = editorBaseUrl();
	if (!base) return null;
	const params = new URLSearchParams({ prompt: input.prompt });
	if (input.remixPlayableId) {
		params.set("remixPlayableId", input.remixPlayableId);
		// Only meaningful next to a source game; alone it would name a workspace
		// after a game that was never imported into it.
		const title = input.remixTitle?.trim();
		if (title) params.set("remixTitle", title);
	}
	if (input.attachments?.length) {
		params.set(
			"attachments",
			JSON.stringify(input.attachments.map(({ url, mime }) => ({ url, mime }))),
		);
	}
	return `${base}/welcome?${params.toString()}`;
}
